"use client"

export interface ConversionProgress {
  stage: "detecting" | "converting" | "validating" | "complete"
  progress: number
  message: string
}

export interface ConversionResult {
  success: boolean
  file?: File
  error?: string
  originalFormat?: string
  convertedFormat?: string
  originalSize?: number
  convertedSize?: number
}

// Enhanced format detection using file signatures
const detectFileFormat = async (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer
      const uint8Array = new Uint8Array(arrayBuffer.slice(0, 12))

      // Check file signatures
      if (uint8Array[0] === 0xff && uint8Array[1] === 0xd8) {
        resolve("image/jpeg")
      } else if (uint8Array[0] === 0x89 && uint8Array[1] === 0x50 && uint8Array[2] === 0x4e && uint8Array[3] === 0x47) {
        resolve("image/png")
      } else if (uint8Array[0] === 0x52 && uint8Array[1] === 0x49 && uint8Array[2] === 0x46 && uint8Array[3] === 0x46) {
        resolve("image/webp")
      } else if (
        // HEIC signature check
        uint8Array[4] === 0x66 &&
        uint8Array[5] === 0x74 &&
        uint8Array[6] === 0x79 &&
        uint8Array[7] === 0x70 &&
        uint8Array[8] === 0x68 &&
        uint8Array[9] === 0x65 &&
        uint8Array[10] === 0x69 &&
        uint8Array[11] === 0x63
      ) {
        resolve("image/heic")
      } else {
        // Fallback to file extension or MIME type
        const fileName = file.name.toLowerCase()
        if (fileName.endsWith(".heic") || fileName.endsWith(".heif")) {
          resolve("image/heic")
        } else {
          resolve(file.type || "unknown")
        }
      }
    }
    reader.readAsArrayBuffer(file.slice(0, 12))
  })
}

const convertHeicToPng = async (file: File, onProgress?: (progress: ConversionProgress) => void): Promise<File> => {
  try {
    if (file.size > VALIDATION_CONFIG.MAX_FILE_SIZE) {
      throw new Error(
        `File size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds maximum allowed size of ${(VALIDATION_CONFIG.MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB`,
      )
    }

    onProgress?.({
      stage: "converting",
      progress: 25,
      message: "Converting HEIC image to PNG...",
    })

    const { default: heic2anyImport } = await import("heic2any")
    const heic2any = heic2anyImport

    const conversionPromise = heic2any({
      blob: file,
      toType: "image/png",
      quality: 1.0, // PNG is lossless, so use max quality
    }) as Promise<Blob>

    // Add timeout to prevent hanging on large files
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Conversion timeout - file may be too large")), 30000) // 30 second timeout
    })

    const convertedBlob = await Promise.race([conversionPromise, timeoutPromise])

    onProgress?.({
      stage: "validating",
      progress: 75,
      message: "Validating converted image...",
    })

    // Create new filename with .png extension
    const originalName = file.name.replace(/\.(heic|heif)$/i, "")
    const newFileName = `${originalName}.png`

    const convertedFile = new File([convertedBlob], newFileName, {
      type: "image/png",
      lastModified: Date.now(),
    })

    onProgress?.({
      stage: "complete",
      progress: 100,
      message: "Conversion complete!",
    })

    return convertedFile
  } catch (error) {
    console.error("[v0] HEIC conversion error:", error)
    throw new Error(`HEIC conversion failed: ${error instanceof Error ? error.message : "Unknown error"}`)
  }
}

const convertToPng = async (file: File, onProgress?: (progress: ConversionProgress) => void): Promise<File> => {
  try {
    if (file.size > VALIDATION_CONFIG.MAX_FILE_SIZE) {
      throw new Error(
        `File size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds maximum allowed size of ${(VALIDATION_CONFIG.MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB`,
      )
    }

    onProgress?.({
      stage: "converting",
      progress: 25,
      message: "Converting image to PNG...",
    })

    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = "anonymous"

      img.onload = () => {
        try {
          onProgress?.({
            stage: "converting",
            progress: 50,
            message: "Processing image data...",
          })

          const canvas = document.createElement("canvas")
          const ctx = canvas.getContext("2d")

          if (!ctx) {
            reject(new Error("Could not create canvas context"))
            return
          }

          canvas.width = img.width
          canvas.height = img.height
          ctx.drawImage(img, 0, 0)

          onProgress?.({
            stage: "validating",
            progress: 75,
            message: "Validating converted image...",
          })

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Failed to convert image to PNG"))
                return
              }

              // Create new filename with .png extension
              const originalName = file.name.replace(/\.[^.]+$/, "")
              const newFileName = `${originalName}.png`

              const convertedFile = new File([blob], newFileName, {
                type: "image/png",
                lastModified: Date.now(),
              })

              onProgress?.({
                stage: "complete",
                progress: 100,
                message: "Conversion complete!",
              })

              resolve(convertedFile)
            },
            "image/png",
            1.0,
          ) // PNG is lossless, use max quality
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => {
        reject(new Error("Failed to load image for conversion"))
      }

      // Create object URL for the image
      const objectUrl = URL.createObjectURL(file)
      img.src = objectUrl

      // Clean up object URL after a delay
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    })
  } catch (error) {
    console.error("[v0] Image conversion error:", error)
    throw new Error(`Image conversion failed: ${error instanceof Error ? error.message : "Unknown error"}`)
  }
}

// Main conversion function
export const convertImageIfNeeded = async (
  file: File,
  onProgress?: (progress: ConversionProgress) => void,
): Promise<ConversionResult> => {
  try {
    onProgress?.({
      stage: "detecting",
      progress: 10,
      message: "Detecting image format...",
    })

    // Detect actual file format
    const detectedFormat = await detectFileFormat(file)
    const originalSize = file.size

    const isPng = detectedFormat === "image/png"
    const isJpeg = detectedFormat === "image/jpeg" || detectedFormat === "image/jpg"

    if (!isPng && !isJpeg) {
      let convertedFile: File

      if (detectedFormat === "image/heic" || file.name.toLowerCase().match(/\.(heic|heif)$/)) {
        // Use heic2any for HEIC files
        convertedFile = await convertHeicToPng(file, onProgress)
      } else {
        // Use Canvas API for other formats (WebP, etc.)
        convertedFile = await convertToPng(file, onProgress)
      }

      return {
        success: true,
        file: convertedFile,
        originalFormat: detectedFormat,
        convertedFormat: "image/png",
        originalSize,
        convertedSize: convertedFile.size,
      }
    }

    // For PNG and JPEG, return as-is
    onProgress?.({
      stage: "complete",
      progress: 100,
      message: "No conversion needed",
    })

    return {
      success: true,
      file,
      originalFormat: detectedFormat,
      convertedFormat: detectedFormat,
      originalSize,
      convertedSize: file.size,
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Conversion failed",
      originalFormat: file.type || "unknown",
      originalSize: file.size,
    }
  }
}

// Validation constants
export const VALIDATION_CONFIG = {
  MAX_FILE_SIZE: 15 * 1024 * 1024, // 15MB (reduced from 20MB to prevent browser crashes)
  ACCEPTED_FORMATS: ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"],
  TARGET_FORMATS: ["image/jpeg", "image/png"], // Updated to reflect that we convert everything else to PNG
}

// Enhanced file validation
export const validateImageFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file size
  if (file.size > VALIDATION_CONFIG.MAX_FILE_SIZE) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1)
    const maxSizeMB = (VALIDATION_CONFIG.MAX_FILE_SIZE / 1024 / 1024).toFixed(0)
    return {
      isValid: false,
      error: `File size ${sizeMB}MB exceeds maximum allowed size of ${maxSizeMB}MB`,
    }
  }

  // Check file type (including HEIC which we can convert)
  const fileName = file.name.toLowerCase()
  const isHeic = fileName.endsWith(".heic") || fileName.endsWith(".heif") || file.type === "image/heic"
  const isAcceptedFormat = VALIDATION_CONFIG.ACCEPTED_FORMATS.includes(file.type) || isHeic

  if (!isAcceptedFormat) {
    return {
      isValid: false,
      error: `Unsupported file format. Please use: JPEG, PNG, WebP, or HEIC`,
    }
  }

  return { isValid: true }
}
